/**
 * Phase 32 — the country's alert channels in one dialog: a Telegram card
 * and a Slack card (each on/off, target, topics, digests, test message).
 * Save writes the channels that changed; viewing needs no permission, saving
 * needs manage (the cards go read-only without it).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useState } from 'react';
import type { DropGenNotificationRow, DropGenNotificationSendResult } from '@trafficpulse/shared';

import { ConfirmDialog } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import { minutesToHHMM } from '../../../utils/format';
import { ChannelCard } from './ChannelCard';
import { type ChannelDraft, draftProblem, isDirty, toBody, toDraft } from './notification-draft';
import { useNotificationMutations, useNotifications } from './use-notifications';

interface Props {
  open: boolean;
  countryKey: string;
  countryName: string;
  recipientTimezone: string | null;
  generateAtMin: number;
  windowEndMin: number;
  onClose: () => void;
}

export const NotificationsDialog: FC<Props> = (props) => {
  const { data } = useNotifications(props.countryKey, props.open);
  const { save, test } = useNotificationMutations(props.countryKey);
  const canManage = useAuth().hasPermission('drop_generator.manage');
  const { success, error } = useToast();
  const [seed, setSeed] = useState<ChannelDraft[]>([]);
  const [drafts, setDrafts] = useState<ChannelDraft[]>([]);
  const [tested, setTested] = useState<Record<string, DropGenNotificationSendResult>>({});

  // Seed once per opening; a later refetch (after a test message) refreshes
  // only the server's facts, never what is being typed.
  useEffect(() => {
    if (!props.open) {
      setSeed([]);
      setDrafts([]);
      setTested({});
      return;
    }
    if (!data) return;
    const facts = (d: ChannelDraft) => {
      const row = data.find((r) => r.channel === d.channel);
      return row ? { ...d, hasTarget: row.hasTarget, targetMasked: row.targetMasked, channelReady: row.channelReady, hasThread: row.hasThread, lastSentAt: row.lastSentAt, lastError: row.lastError } : d;
    };
    setSeed((s) => (s.length === 0 ? data.map(toDraft) : s.map(facts)));
    setDrafts((s) => (s.length === 0 ? data.map(toDraft) : s.map(facts)));
  }, [props.open, data]);

  const patch = (channel: string, p: Partial<ChannelDraft>) => setDrafts((all) => all.map((d) => (d.channel === channel ? { ...d, ...p } : d)));
  const problem = drafts.map(draftProblem).find(Boolean) ?? null;
  const changed = drafts.filter((d, i) => seed[i] && isDirty(d, seed[i]));

  const onSave = async () => {
    try {
      for (const d of changed) await save.mutateAsync({ channel: d.channel.toLowerCase(), body: toBody(d) });
      success('Notifications saved', changed.length === 0 ? 'Nothing had changed.' : `${changed.map((d) => (d.channel === 'TELEGRAM' ? 'Telegram' : 'Slack')).join(' and ')} saved.`);
      props.onClose();
    } catch (err) {
      error('Could not save the notifications', getApiErrorMessage(err));
    }
  };
  const onTest = async (channel: string) => {
    try {
      const result = await test.mutateAsync(channel.toLowerCase());
      setTested((t) => ({ ...t, [channel]: result }));
    } catch (err) {
      setTested((t) => ({ ...t, [channel]: { ok: false, error: getApiErrorMessage(err) } }));
    }
  };

  return (
    <ConfirmDialog
      open={props.open}
      title={`Notifications — ${props.countryName}`}
      widthClass="max-w-2xl"
      description={
        <div className="flex flex-col gap-4 pt-2">
          <p className="text-xs text-muted-foreground">
            The Live feed&apos;s decisions, sent where you read them — as they happen, plus two daily summaries{props.recipientTimezone ? ` · times in Danish time first, then ${props.recipientTimezone}` : ''}.
          </p>
          {drafts.map((d) => (
            <ChannelCard
              key={d.channel}
              draft={d}
              countryName={props.countryName}
              recipientTimezone={props.recipientTimezone}
              defaultDigestAt={minutesToHHMM((props.generateAtMin + 5) % 1440)}
              windowEnd={minutesToHHMM(props.windowEndMin)}
              canManage={canManage}
              testing={test.isPending && test.variables === d.channel.toLowerCase()}
              testResult={tested[d.channel] ?? null}
              onChange={(p) => patch(d.channel, p)}
              onTest={() => onTest(d.channel)}
            />
          ))}
          {!data ? <p className="text-xs text-muted-foreground">Loading the channels…</p> : null}
          {problem ? <p className="text-xs font-medium text-warning">{problem}</p> : null}
          {!canManage ? <p className="text-xs text-muted-foreground">You can read these settings; changing them needs the manage permission.</p> : null}
        </div>
      }
      confirmLabel="Save"
      confirmDisabled={!canManage || problem !== null}
      busy={save.isPending}
      onConfirm={onSave}
      onCancel={props.onClose}
    />
  );
};

export type { DropGenNotificationRow };
